import { css } from 'aphrodite';
import * as React from 'react';
import { HandwrittenCardProvider } from '../../../../extViewmodels';
import { useTemplateHwcCategoryNames } from '../../../../queries';
import { Checkbox } from '../../../../web/components/Checkbox';
import { DeprecatedTextArea } from '../../../../web/components/DeprecatedTextArea';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Input } from '../../../../web/components-v2/Input';
import { PostcardTextEditor } from '../../../../web/containers/PostcardComposer/PostcardTextEditor';
import { HANDWRITTEN_CARD_TEXT_MAX_ALLOWED_CHARACTERS } from '../../../../web/containers/PostcardComposer/hooks/usePostcardTextEditor';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { ImagePreviewUploader } from '../../ImagePreviewUploader';
import { IndustryCategorySection } from '../IndustryCategorySection';
import { useHwcTemplateForm } from './hooks/useHwcTemplateForm';
import { styleSheet } from './styles';

type IProps = ReturnType<typeof useHwcTemplateForm> & {
	onSave: () => void;
	onPublish: () => void;
	mutationInProgress?: boolean;
};

export function HwcTemplateForm({
	dispatch,
	displayName,
	featured,
	getPostcardTextEditorProps,
	keywords,
	mutationInProgress = false,
	onPublish,
	onSave,
	postcardText,
	providerProductIds,
	selectedIndustryOptions,
	summary,
	templateImagePreviewURL,
}: IProps) {
	const categoryOptionsQuery = useTemplateHwcCategoryNames();
	const handleImageUpload = (ev: React.ChangeEvent<HTMLInputElement>) => {
		const file = ev.target.files[0];
		if (file) {
			dispatch({ type: 'SET_TEMPLATE_IMAGE_FILE', value: file });
		}
	};

	return (
		<div className={css(styleSheet.root)}>
			<h1 className={css(styleSheet.headerLabel, styleSheet.headerDisplayName)}>
				Create New Handwritten Card Template
			</h1>
			<div className={css(styleSheet.formControlWrap)}>
				<label
					className={css(styleSheet.labelDisplayName, styleSheet.labelRequired)}
					htmlFor='create-hwc-template-name-input'
				>
					Display Name:
				</label>
				<Input
					id='create-hwc-template-name-input'
					type='text'
					value={displayName}
					onChange={ev => dispatch({ type: 'SET_DISPLAY_NAME', value: ev.target.value })}
				/>
			</div>
			<div className={css(styleSheet.bodyCenter)}>
				<div className={css(styleSheet.sectionCustomDesign)}>
					<div className={css(styleSheet.sectionCustomDesignHeader)}>
						<label className={css(styleSheet.headerLabel, styleSheet.sectionCustomDesignHeaderLabel)}>
							Custom Design
						</label>
						<span className={css(styleSheet.sectionCustomDesignHeaderMax2GB)}>Max 2GB</span>
					</div>
					<ImagePreviewUploader
						id='create-hwc-template-image-input'
						onChange={handleImageUpload}
						previewImageUrl={templateImagePreviewURL}
						onPreviewImageLoaded={() => URL.revokeObjectURL(templateImagePreviewURL)}
					/>
					<Checkbox
						id='feature-hwc-checkbox'
						className={css(styleSheet.featureCheckbox)}
						checked={featured}
						onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
							dispatch({ type: 'SET_FEATURED', value: ev.target.checked })
						}
					>
						<span>Feature this card</span>
					</Checkbox>
				</div>
				<div className={css(styleSheet.sectionMessage)}>
					<div className={css(styleSheet.sectionCustomDesignHeader, styleSheet.sectionCustomDesignMessage)}>
						<label className={css(styleSheet.headerLabel, styleSheet.sectionCustomDesignHeaderLabel)}>Message</label>
						<span
							className={css(
								styleSheet.sectionMessageCharCount,

								postcardText.length > HANDWRITTEN_CARD_TEXT_MAX_ALLOWED_CHARACTERS &&
									styleSheet.sectionMessageCharCountOver
							)}
						>
							{postcardText.length}/{HANDWRITTEN_CARD_TEXT_MAX_ALLOWED_CHARACTERS}
						</span>
					</div>
					<div className={css(styleSheet.formControlWrap)}>
						<PostcardTextEditor {...getPostcardTextEditorProps()} />
					</div>
					{Object.keys(HandwrittenCardProvider)
						.filter(x => x !== HandwrittenCardProvider.SimplyNoted)
						.map(x => (
							<>
								<div className={css(styleSheet.sectionCustomDesignHeader)}>
									<label className={css(styleSheet.headerLabel, styleSheet.sectionCustomDesignHeaderLabel)}>
										{x} Product ID
									</label>
								</div>
								<Input
									styles={[styleSheet.providerTextbox]}
									key={x}
									id={`create-hwc-template-prodct-id-input-${x}`}
									type='text'
									value={providerProductIds[x as HandwrittenCardProvider]}
									onChange={ev =>
										dispatch({
											type: 'SET_PRODUCT_ID',
											value: { provider: x as HandwrittenCardProvider, id: ev.target.value },
										})
									}
								/>
							</>
						))}
				</div>
			</div>
			<div className={css(styleSheet.sectionIndustries)}>
				{categoryOptionsQuery.data ? (
					<IndustryCategorySection
						categoryOptions={categoryOptionsQuery.data}
						selectedOptions={selectedIndustryOptions}
						onSelectBoxChanged={industry => selectedOption => {
							dispatch({
								type: 'ADD_TO_INDUSTRY_OPTIONS',
								value: { categoryName: selectedOption.value, industry, catOptions: categoryOptionsQuery.data },
							});
						}}
						onCheckboxChanged={industry => ev => {
							if (!ev.target.checked) {
								dispatch({ type: 'REMOVE_FROM_INDUSTRY_OPTIONS', value: { industry } });
							}
						}}
					/>
				) : (
					<LoadingSpinner type='small' />
				)}
			</div>
			<div className={css(styleSheet.formControlWrap)}>
				<label
					htmlFor='create-hwc-template-summary-input'
					className={css(styleSheet.headerLabel, styleSheet.headerLabel)}
				>
					Card Summary
				</label>
				<DeprecatedTextArea
					inputId='create-hwc-template-summary-input'
					value={summary}
					onChange={ev => dispatch({ type: 'SET_SUMMARY', value: ev.target.value })}
				/>
			</div>
			<div className={css(styleSheet.formControlWrap)}>
				<label
					htmlFor='create-hwc-template-keywords-input'
					className={css(styleSheet.headerLabel, styleSheet.headerLabel)}
				>
					Keywords
				</label>
				<Input
					styles={[styleSheet.keywordsField]}
					id='create-hwc-template-keywords-input'
					placeholder='Keywords (separated by comma)'
					type='text'
					value={keywords}
					onChange={ev => dispatch({ type: 'SET_KEYWORDS', value: ev.target.value })}
				/>
			</div>
			<div className={css(styleSheet.footer)}>
				<button className={css(baseStyleSheet.ctaButton)} onClick={onSave} disabled={mutationInProgress || featured}>
					Save
				</button>
				<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onPublish} disabled={mutationInProgress}>
					Save & Publish
				</button>
			</div>
		</div>
	);
}
